import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Dropdown } from "react-bootstrap";
import cx from "classnames";
import InfiniteScroll from "src/components/infiniteScroll";
import TruncateWithTooltip from "src/components/truncateWithTooltip";
import styles from "./dropdown.module.scss";

interface DropDownProps {
  selected: {
    key: string;
    label: string;
  };
  options: Array<{ key: string; label: string }>;
  onChange: (key: string) => void;
  icon?: string;
  placeholder?: string;
  loadingLabel?: string;
  emptyLabel?: string;
  loading?: boolean;
  hasMore?: boolean;
  loadMore?: () => void;
  className?: string;
  align?: "start" | "end";
  showSelectionIcon?: boolean;
  disabled?: boolean;
  matchToggleWidth?: boolean;
  hideWhenSingleOption?: boolean;
  unconstrainedWidth?: boolean;
}

export const DropDown: React.FC<DropDownProps> = ({
  selected,
  options,
  onChange,
  icon,
  placeholder,
  loadingLabel = "Loading...",
  emptyLabel,
  loading,
  hasMore,
  loadMore,
  className,
  align,
  showSelectionIcon,
  disabled,
  matchToggleWidth,
  hideWhenSingleOption,
  unconstrainedWidth,
}) => {
  const supportsInfiniteScroll = typeof loadMore === "function";
  const selectedOption = useMemo(() => {
    return options.find((option) => option.key === selected.key);
  }, [selected, options]);

  const [show, setShow] = useState(false);
  const [showBottomFade, setShowBottomFade] = useState(false);
  const scrollElRef = useRef<HTMLDivElement | null>(null);

  const updateBottomFade = useCallback((el: HTMLDivElement | null) => {
    if (!el) {
      setShowBottomFade(false);
      return;
    }
    const isScrollable = el.scrollHeight > el.clientHeight;
    const atBottom =
      el.scrollHeight - el.scrollTop - el.clientHeight <= 1;
    setShowBottomFade(isScrollable && !atBottom);
  }, []);

  const scrollRef = useCallback(
    (el: HTMLDivElement | null) => {
      scrollElRef.current = el;
      updateBottomFade(el);
    },
    [updateBottomFade],
  );

  const handleScroll = useCallback(
    (e: React.UIEvent<HTMLDivElement>) => {
      updateBottomFade(e.currentTarget);
    },
    [updateBottomFade],
  );

  useEffect(() => {
    const el = scrollElRef.current;
    if (!show || !el) {
      setShowBottomFade(false);
      return;
    }

    updateBottomFade(el);

    const observer = new ResizeObserver(() => updateBottomFade(el));
    observer.observe(el);
    return () => observer.disconnect();
  }, [show, options, loading, updateBottomFade]);

  const handleSelect = (key: string) => {
    if (onChange) {
      onChange(key);
    }
  };

  const isSingleOption =
    !!hideWhenSingleOption &&
    !loading &&
    !supportsInfiniteScroll &&
    options.length <= 1;
  const isInteractive = !disabled && !isSingleOption;

  const showMenu = loading || options.length >= 1 || emptyLabel !== undefined;

  const renderItem = (option: { key: string; label: string }, index: number) => {
    const isSelected = option.key === selectedOption?.key;
    return (
      <Dropdown.Item
        key={index}
        className={cx({ [styles.active]: isSelected })}
        onClick={() => handleSelect(option.key)}
      >
        {showSelectionIcon && (
          <i
            className={cx(
              styles.selectIcon,
              isSelected
                ? cx("fa-solid fa-check-circle", styles.activeSelect)
                : "paperflite-radio-button-unselected",
            )}
          />
        )}
        <TruncateWithTooltip
          className={styles.itemLabel}
          placement="top"
          text={option.label}
        />
      </Dropdown.Item>
    );
  };

  return (
    <Dropdown
      show={isInteractive ? show : false}
      onToggle={() => {
        if (!isInteractive) return;
        setShow(!show);
      }}
      className={cx(
        styles.dropdown,
        {
          [styles.disabled]: disabled,
          [styles.singleOption]: isSingleOption,
          [styles.unconstrainedWidth]: unconstrainedWidth,
        },
        className,
      )}
    >
      <Dropdown.Toggle>
        {icon && <i className={cx(styles.leadingIcon, icon)} />}
        <TruncateWithTooltip
          className={styles.toggleLabel}
          placement="top"
          text={selectedOption?.label || selected.label || placeholder || ""}
        />
        {!isSingleOption && (
          <i
            className={cx("paperflite-dropdown", { [styles.openModal]: show })}
          />
        )}
      </Dropdown.Toggle>

      {isInteractive && showMenu && (
        <Dropdown.Menu
          align={align}
          className={cx({ [styles.matchToggleWidth]: matchToggleWidth })}
          renderOnMount
          popperConfig={{
            strategy: "fixed",
            modifiers: [
              {
                name: "offset",
                options: {
                  offset: [0, 6],
                },
              },
              {
                name: "preventOverflow",
                options: {
                  altAxis: true,
                  padding: 8,
                },
              },
              ...(matchToggleWidth
                ? [
                    {
                      name: "sameWidth",
                      enabled: true,
                      phase: "beforeWrite" as const,
                      requires: ["computeStyles"],
                      fn: ({ state }: { state: any }) => {
                        state.styles.popper.width = `${state.rects.reference.width}px`;
                      },
                      effect: ({ state }: { state: any }) => {
                        state.elements.popper.style.width = `${
                          (state.elements.reference as HTMLElement).offsetWidth
                        }px`;
                      },
                    },
                  ]
                : []),
            ],
          }}
        >
          {loading && options.length === 0 ? (
            <Dropdown.Item disabled className={styles.empty}>
              <span>{loadingLabel}</span>
            </Dropdown.Item>
          ) : options.length === 0 ? (
            <Dropdown.Item disabled className={styles.empty}>
              <span>{emptyLabel}</span>
            </Dropdown.Item>
          ) : supportsInfiniteScroll ? (
            <div
              ref={scrollRef}
              onScroll={handleScroll}
              className={cx(styles.scrollable, {
                [styles.fadeBottom]: showBottomFade,
              })}
            >
              <InfiniteScroll
                className={styles.scrollableList}
                loading={loading}
                hasMore={hasMore}
                loadMore={loadMore!}
              >
                {options.map((option, index) => renderItem(option, index))}
              </InfiniteScroll>
            </div>
          ) : (
            <div
              ref={scrollRef}
              onScroll={handleScroll}
              className={cx(styles.scrollable, styles.scrollableList, {
                [styles.fadeBottom]: showBottomFade,
              })}
            >
              {options.map((option, index) => renderItem(option, index))}
            </div>
          )}
        </Dropdown.Menu>
      )}
    </Dropdown>
  );
};
